import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Spin, message, Progress,
} from 'antd';
import {
  PieChartOutlined, BarChartOutlined,
} from '@ant-design/icons';
import { adminAPI, adminUsersAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';

const { Title, Text } = Typography;

const AdminStatsPage: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [complianceStats, setComplianceStats] = useState<any>(null);
  const [dashboardStats, setDashboardStats] = useState<any>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [compRes, dashRes] = await Promise.all([
        adminAPI.getStats(),
        adminUsersAPI.getDashboard(),
      ]);
      setComplianceStats(compRes.data);
      setDashboardStats(dashRes.data);
    } catch {
      message.error(t('adminPanel.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  const tierData = complianceStats?.by_tier || {};
  const availData = complianceStats?.by_availability || {};
  const total = complianceStats?.total || 1;

  const tierItems = [
    { key: 'T1_full', label: t('admin.t1Count'), color: '#52c41a' },
    { key: 'T2_partial', label: t('admin.t2Count'), color: '#faad14' },
    { key: 'T3_none', label: t('admin.t3Count'), color: '#f5222d' },
    { key: 'pending', label: 'Pending', color: '#8c8c8c' },
  ];

  const availItems = [
    { key: 'cn_native', label: t('admin.cnNative'), color: '#52c41a' },
    { key: 'direct', label: t('admin.directAccess'), color: '#1677ff' },
    { key: 'mirror', label: 'Mirror', color: '#13c2c2' },
    { key: 'vpn', label: 'VPN', color: '#faad14' },
    { key: 'blocked', label: 'Blocked', color: '#f5222d' },
    { key: 'unknown', label: t('common.unknown'), color: '#8c8c8c' },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>{t('adminPanel.statsTitle')}</Title>

      {/* System Overview */}
      {dashboardStats && (
        <Card title={t('adminPanel.systemOverview')} style={{ borderRadius: 12, marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            <Col xs={8} sm={4}>
              <Statistic title={t('adminPanel.totalTools')} value={dashboardStats.totals.tools} />
            </Col>
            <Col xs={8} sm={4}>
              <Statistic title={t('adminPanel.activeTools')} value={dashboardStats.totals.activeTools} />
            </Col>
            <Col xs={8} sm={4}>
              <Statistic title={t('adminPanel.featuredTools')} value={dashboardStats.totals.featuredTools} />
            </Col>
            <Col xs={8} sm={4}>
              <Statistic title={t('adminPanel.totalUsers')} value={dashboardStats.totals.users} />
            </Col>
            <Col xs={8} sm={4}>
              <Statistic title={t('adminPanel.totalReviews')} value={dashboardStats.totals.reviews} />
            </Col>
            <Col xs={8} sm={4}>
              <Statistic title={t('adminPanel.totalFavorites')} value={dashboardStats.totals.favorites} />
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        {/* Compliance Tier Distribution */}
        <Col xs={24} md={12}>
          <Card
            title={<><PieChartOutlined /> {t('adminPanel.tierDistribution')}</>}
            style={{ borderRadius: 12 }}
          >
            {tierItems.map(item => {
              const count = tierData[item.key] || 0;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={item.key} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text>{item.label}</Text>
                    <Text strong>{count} ({pct}%)</Text>
                  </div>
                  <Progress
                    percent={pct}
                    strokeColor={item.color}
                    showInfo={false}
                    size="small"
                  />
                </div>
              );
            })}
          </Card>
        </Col>

        {/* Availability Distribution */}
        <Col xs={24} md={12}>
          <Card
            title={<><BarChartOutlined /> {t('adminPanel.availDistribution')}</>}
            style={{ borderRadius: 12 }}
          >
            {availItems.map(item => {
              const count = availData[item.key] || 0;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={item.key} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text>{item.label}</Text>
                    <Text strong>{count} ({pct}%)</Text>
                  </div>
                  <Progress
                    percent={pct}
                    strokeColor={item.color}
                    showInfo={false}
                    size="small"
                  />
                </div>
              );
            })}
          </Card>
        </Col>
      </Row>

      {/* 7-day Trends */}
      {dashboardStats && (
        <Card title={t('adminPanel.recent7d')} style={{ borderRadius: 12, marginTop: 16 }}>
          <Row gutter={24}>
            <Col span={8}>
              <Statistic
                title={t('adminPanel.newTools7d')}
                value={dashboardStats.recent7d.newTools}
                valueStyle={{ color: '#1677ff' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={t('adminPanel.newUsers7d')}
                value={dashboardStats.recent7d.newUsers}
                valueStyle={{ color: '#722ed1' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={t('adminPanel.newReviews7d')}
                value={dashboardStats.recent7d.newReviews}
                valueStyle={{ color: '#13c2c2' }}
              />
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default AdminStatsPage;
